import { useContext } from "react"
import { AlertContext } from "../contexts/AlertContext";


export const useAlert = () => {
  const { alertState, setAlertState } = useContext(AlertContext);

  const openAlert = (title, message, type, okClick, cancelClick, options = [], disableBackdrop = false) => {
    setAlertState({ open: true, title, message, type, options, okClick, cancelClick, disableBackdrop });
  }

  /**
  * Displays a success alert with the given title, message, and additional properties.
  * 
  * @param {string} title - The title of the alert.
  * @param {string} message - The message to display in the alert.
  * @param {Object} [additionalProps] - An optional object with additional properties.
  * @param {Function} [additionalProps.okClick] - Function to attach to the "OK" button click event.
  * @param {Function} [additionalProps.cancelClick] - Handles "Cancel" button click; hides button if undefined.
  * @param {boolean} [additionalProps.disableBackdrop] - Prevents the alert from closing when clicking on the backdrop, allowing closure only through button clicks.
  * @param {Array} [additionalProps.options] - Array of option objects to override default "Ok"/"Cancel" buttons.
  *        Each option object structure:
  *          { label: string, onClick: Function, type: string (optional, 'danger' for red button) }
  */
  const successAlert = (title, message, additionalProps) => {
    let { okClick, cancelClick, options, disableBackdrop } = additionalProps;
    openAlert(title, message, "success", okClick, cancelClick, options, disableBackdrop);
  }


  /**
  * Displays a error alert with the given title, message, and additional properties.
  * 
  * @param {string} title - The title of the alert.
  * @param {string} message - The message to display in the alert.
  * @param {Object} [additionalProps] - An optional object with additional properties.
  * @param {Function} [additionalProps.okClick] - Function to attach to the "OK" button click event.
  * @param {Function} [additionalProps.cancelClick] - Handles "Cancel" button click; hides button if undefined.
  * @param {boolean} [additionalProps.disableBackdrop] - Prevents the alert from closing when clicking on the backdrop, allowing closure only through button clicks.
  * @param {Array} [additionalProps.options] - Array of option objects to override default "Ok"/"Cancel" buttons.
  *        Each option object structure:
  *          { label: string, onClick: Function, type: string (optional, 'danger' for red button) }
  */
  const errorAlert = (title, message, additionalProps) => {
    let { okClick, cancelClick, options, disableBackdrop } = additionalProps;
    openAlert(title, message, "error", okClick, cancelClick, options, disableBackdrop);
  }


  /**
  * Displays a warning alert with the given title, message, and additional properties.
  * 
  * @param {string} title - The title of the alert.
  * @param {string} message - The message to display in the alert.
  * @param {Object} [additionalProps] - An optional object with additional properties.
  * @param {Function} [additionalProps.okClick] - Function to attach to the "OK" button click event.
  * @param {Function} [additionalProps.cancelClick] - Handles "Cancel" button click; hides button if undefined.
  * @param {boolean} [additionalProps.disableBackdrop] - Prevents the alert from closing when clicking on the backdrop, allowing closure only through button clicks.
  * @param {Array} [additionalProps.options] - Array of option objects to override default "Ok"/"Cancel" buttons.
  *        Each option object structure:
  *          { label: string, onClick: Function, type: string (optional, 'danger' for red button) }
  */
  const warningAlert = (title, message, additionalProps) => {
    let { okClick, cancelClick, options, disableBackdrop } = additionalProps;
    openAlert(title, message, "warning", okClick, cancelClick, options, disableBackdrop);
  }


  /**
  * Displays a info alert with the given title, message, and additional properties.
  * 
  * @param {string} title - The title of the alert.
  * @param {string} message - The message to display in the alert.
  * @param {Object} [additionalProps] - An optional object with additional properties.
  * @param {Function} [additionalProps.okClick] - Function to attach to the "OK" button click event.
  * @param {Function} [additionalProps.cancelClick] - Handles "Cancel" button click; hides button if undefined.
  * @param {boolean} [additionalProps.disableBackdrop] - Prevents the alert from closing when clicking on the backdrop, allowing closure only through button clicks.
  * @param {Array} [additionalProps.options] - Array of option objects to override default "Ok"/"Cancel" buttons.
  *        Each option object structure:
  *          { label: string, onClick: Function, type: string (optional, 'danger' for red button) }
  */
  const infoAlert = (title, message, additionalProps) => {
    let { okClick, cancelClick, options, disableBackdrop } = additionalProps;
    openAlert(title, message, "info", okClick, cancelClick, options, disableBackdrop);
  }


  return ({ successAlert, infoAlert, warningAlert, errorAlert });

}